import React, { useContext, useMemo } from 'react';
import { PlateInfoDTO } from 'business/models/Models';
import ProjectSidePanelContext from './projectSidePanelContext';
import { useFetchPlateNodeCustomNameProperty } from './hooks';
import EditBarcode from './EditBarcode';
import useRMLite from 'CustomHooks/useRMLite';
import { EPlateState } from 'enums/EPlatestate';
import { EPlateWellStates } from 'enums/EPlateWellStates';
import './ExperimentPlateInfoPanel.scss';
import { ReactComponent as ActiveIcon } from 'Icons/PlateIcons/active.svg';
import { ReactComponent as ActiveDispensedIcon } from 'Icons/PlateIcons/active-dispensed.svg';
import { ReactComponent as ActiveHarvestedIcon } from 'Icons/PlateIcons/active-harvested.svg';
import { ReactComponent as ActiveHitsIcon } from 'Icons/PlateIcons/active-hits.svg';
import { ReactComponent as ActiveImagedIcon } from 'Icons/PlateIcons/active-imaged.svg';
import { ReactComponent as ExpiredIcon } from 'Icons/PlateIcons/expired.svg';
import { ReactComponent as ExpiredHarvestedIcon } from 'Icons/PlateIcons/expired-harvested.svg';
import { ReactComponent as ExpiredHitsIcon } from 'Icons/PlateIcons/expired-hits.svg';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import { renderPlateChip } from 'utils/PlateUtils';

export const getPlateIconComponent = (
  isExpired: boolean,
  wellState: EPlateWellStates,
  isSeeded: boolean,
) => {
  switch (wellState) {
    case EPlateWellStates.Dispensed:
      return isExpired ? ExpiredIcon : ActiveDispensedIcon;
    case EPlateWellStates.Imaged:
      return isExpired ? ExpiredIcon : ActiveImagedIcon;
    case EPlateWellStates.Hits:
      return isExpired ? ExpiredHitsIcon : ActiveHitsIcon;
    case EPlateWellStates.Harvested:
      return isExpired ? ExpiredHarvestedIcon : ActiveHarvestedIcon;
    case EPlateWellStates.Designed:
    default:
      return isExpired ? ExpiredIcon : ActiveIcon;
  }
};

export interface ExperimentPlateInfoPanelProps {
  expDTO: PlateInfoDTO[];
  experimentId: number;
  plates: any[];
  isEditBarcodeAllowed: boolean;
}

let plateInfoKeys = ['barcode', 'id', 'plateNumber'];
const returnData = (plate, plateNodeNameFormatProperty) => {
  let res = '';
  if (plateNodeNameFormatProperty) {
    plateInfoKeys = plateNodeNameFormatProperty.value
      .split(',')
      .map((item) =>
        item
          .replace(/[<>]/g, '')
          .trim()
          .replace('plateNum', 'plateNumber')
          .replace('plateID', 'id'),
      )
      .filter((item) => item !== '');
  }

  for (const key of plateInfoKeys) {
    if (plate[key]) {
      res += plate[key] + ', ';
    }
  }
  res = res.substring(0, res.length - 2);
  return res;
};
const plateClickHandler = (experimentId, selectedPlate) => () => {
  if (experimentId) {
    const link = `/exp/${experimentId}/plate/${selectedPlate.id}`;
    window.open(link, link);
  }
};

export const selectPlate = (plate, setSelectedPlateList, isMultiSelect: boolean) => {
  setSelectedPlateList((prev) => {
    const isSelected = prev.some((p) => p.id === plate.id);

    if (isMultiSelect) {
      return isSelected ? prev.filter((p) => p.id !== plate.id) : [...prev, plate];
    }

    return isSelected && prev.length === 1 ? [] : [plate];
  });
};

export const renderPlateIcon = (plate: PlateInfoDTO) => {
  const isExpired = !!plate.isExpired;
  const wellState = plate.wellState ?? EPlateWellStates.Designed;
  const PlateIcon = getPlateIconComponent(isExpired, wellState, plate.hasSeedLayer);
  const iconId = `experimentplateinfopanel-expinfo-${isExpired ? 'expired' : 'active'}`;

  return (
    <div className="plate-icon-wrapper mt-2">
      <PlateIcon id={iconId} />
      {!!plate.hasSeedLayer && (
        <span
          id={`experimentplateinfopanel-expinfo-seeded-${plate.id}`}
          className="plate-seed-badge"
          title="Seeded"
        >
          <RMSvgIcon name="Seed" width={14} height={14} fill="#2E7D32" />
        </span>
      )}
    </div>
  );
};

const renderPlate = (
  plate,
  index,
  experimentId,
  selectedPlateList,
  setSelectedPlateList,
  plateNodeNameFormatProperty,
) => {
  const plateInfo = returnData(plate, plateNodeNameFormatProperty);
  const imagerName = plate.robotName;
  return (
    <li
      key={`exp-plate-${plate.id ?? index}`}
      id={`experimentplateinfopanel-expinfo-${plateInfo.replace(/\s+/g, '-')}-${index}`}
      className="list-item-container exp-plate-info-item node"
      title={plateInfo}
    >
      <div
        id={`experimentplateinfopanel-expinfo-content-${plate.id}`}
        className={`list-content flex-xy-center flex-column
          ${experimentId ? 'cursor-pointer' : ''}
          ${
            selectedPlateList.some((plt) => plt.id === plate.id)
              ? 'selected-explorer-plate'
              : 'transparent-background'
          }`}
        onClick={(e) => selectPlate(plate, setSelectedPlateList, e.ctrlKey || e.metaKey)}
        onDoubleClick={plateClickHandler(experimentId, plate)}
      >
        {renderPlateIcon(plate)}
        <p
          id={`experimentplateinfopanel-expinfo-plate-text-${plate.id}`}
          className="noSelect-plate nodeText"
        >
          {plateInfo}
        </p>
        <div
          id={`experimentplateinfopanel-expinfo-plate-text-${
            imagerName ||
            (plate.plateState === EPlateState.Expired ? 'expired' : 'not-imaged')
          }-${plate.id}`}
          className="exp-plate-chip-container"
        >
          {renderPlateChip(plate, true)}
        </div>
      </div>
    </li>
  );
};

const NOT_IN_IMAGER_HEADER = 'Not in the imager';

export interface ImagerPlateGroup {
  imagerName: string;
  plates: PlateInfoDTO[];
}

export const groupPlatesByImager = (plates: PlateInfoDTO[]): ImagerPlateGroup[] => {
  const groupsByImager = new Map<string, PlateInfoDTO[]>();
  const unloadedPlates: PlateInfoDTO[] = [];

  for (const plate of plates) {
    if (!plate.robotName) {
      unloadedPlates.push(plate);
      continue;
    }
    if (!groupsByImager.has(plate.robotName)) {
      groupsByImager.set(plate.robotName, []);
    }
    groupsByImager.get(plate.robotName).push(plate);
  }

  const groups = Array.from(groupsByImager, ([imagerName, imagerPlates]) => ({
    imagerName,
    plates: imagerPlates,
  }));
  if (unloadedPlates.length > 0) {
    groups.push({ imagerName: NOT_IN_IMAGER_HEADER, plates: unloadedPlates });
  }
  return groups;
};

const getExpInfo = (
  expDTO: PlateInfoDTO[],
  experimentId,
  selectedPlateList,
  setSelectedPlateList,
  plateNodeNameFormatProperty,
) => {
  return (
    <>
      {groupPlatesByImager(expDTO).map((group) => (
        <React.Fragment key={`imager-group-${group.imagerName}`}>
          <div className="imager-header">{group.imagerName}</div>

          <ul className="plate-grid">
            {group.plates.map((plate, index) =>
              renderPlate(
                plate,
                index,
                experimentId,
                selectedPlateList,
                setSelectedPlateList,
                plateNodeNameFormatProperty,
              ),
            )}
          </ul>
        </React.Fragment>
      ))}
    </>
  );
};

const ExperimentPlateInfoPanel = (props: ExperimentPlateInfoPanelProps) => {
  const { expDTO, experimentId, isEditBarcodeAllowed } = props;
  const projectSidePanelContext = useContext(ProjectSidePanelContext);
  const plateNodeNameFormatProperty = useFetchPlateNodeCustomNameProperty({
    experimentId,
  });
  const rmLite = useRMLite();

  const selectedPlate = useMemo(() => {
    if (projectSidePanelContext.selectedPlateList.length !== 1) {
      return null;
    }

    const selectedId = projectSidePanelContext.selectedPlateList[0].id;

    return expDTO.find((p) => p.id === selectedId) || null;
  }, [expDTO, projectSidePanelContext.selectedPlateList]);

  return (
    <div id="experimentplateinfopanel-wrapper" className="py-2 px-3 flex flex-col h-4/5">
      <div
        id="experimentplateinfopanel-header"
        className="nodeInfo nodeLabel flex flex-row w-full p-[1] justify-between items-center shrink-0"
      >
        <div id="experimentplateinfopanel-title" className="flex flex-row">
          <div id="experimentplateinfopanel-label" className="mr-1">
            Plate Preview
          </div>
          (
          <div id="experimentplateinfopanel-count" className=" text-[#006af5]">
            {expDTO.length}
          </div>
          )
        </div>
        {!rmLite && (
          <div id="experimentplateinfopanel-editbarcode">
            <EditBarcode
              plate={selectedPlate}
              setSelectedPlateList={projectSidePanelContext.setSelectedPlateList}
              onEditPlateBarcode={projectSidePanelContext.onEditPlateBarcode}
              disabled={
                !isEditBarcodeAllowed ||
                rmLite ||
                !selectedPlate ||
                selectedPlate.plateState >= EPlateState.Dispensed
              }
            />
          </div>
        )}
      </div>
      <div id="experimentplateinfopanel-content" className="exp-plate-info-container">
        {getExpInfo(
          expDTO,
          experimentId,
          projectSidePanelContext.selectedPlateList,
          projectSidePanelContext.setSelectedPlateList,
          plateNodeNameFormatProperty,
        )}
      </div>
    </div>
  );
};

export default ExperimentPlateInfoPanel;
